import { Button, Chip, Modal, Skeleton } from "@heroui/react";
import { useState } from "react";

import { AccountBadge } from "@/src/components/AccountBadge";
import type { AccountFace } from "@/src/components/AccountBadge";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import type { Locale } from "@/src/i18n/translate";
import { relativeTime } from "@/src/libs/relativeTime";
import { useReportContext } from "@/src/pages/Reports/hooks/useReportContext";
import type {
  MessageReaction,
  ReportContextMessage,
} from "@/src/pages/Reports/reports.type";

/**
 * L'auteur d'un message, dans la forme du badge.
 *
 * `null` quand le compte a été supprimé : le serveur met alors `accountId`
 * **et** `username` à `null`, et un badge sans identifiant n'a pas de fiche à
 * ouvrir. Le message, lui, reste dans la fenêtre.
 */
function author(message: ReportContextMessage): AccountFace | null {
  if (message.accountId === null || message.username === null) return null;

  return {
    avatarUrl: message.avatarUrl,
    id: message.accountId,
    username: message.username,
  };
}

/**
 * La conversation autour du message signalé.
 *
 * Une phrase seule ne se juge pas : « dégage d'ici » est une insulte, une vanne
 * entre habitués, ou la citation de ce qu'un troisième vient d'écrire. La
 * fenêtre montre ce qui précède et ce qui suit, le message visé surligné.
 *
 * La conversation n'est atteignable **que** par le signalement — le serveur
 * n'expose pas son identifiant. Il n'y a donc rien à naviguer d'ici, et c'est
 * exactement ce qui empêche d'ouvrir une conversation que personne n'a
 * signalée, jeton d'administrateur ou pas.
 */
export function ConversationDialog({ reportId }: { reportId: number }) {
  const [isOpen, setOpen] = useState(false);
  const t = useTranslations("reports");

  return (
    <Modal isOpen={isOpen} onOpenChange={setOpen}>
      <Button size="sm" variant="ghost">
        {t("context-open")}
      </Button>

      <Modal.Backdrop>
        <Modal.Container scroll="inside" size="lg">
          <Modal.Dialog>
            <Modal.Header>
              <Modal.Heading>{t("context-title")}</Modal.Heading>
              <Modal.CloseTrigger aria-label={t("context-close")} />
            </Modal.Header>

            <Modal.Body className="flex flex-col gap-3">
              {/*
                Monté **seulement** à l'ouverture, et c'est ce qui tient la
                promesse faite au serveur : sans ce montage conditionnel, le
                rendu de la liste irait chercher le contexte des dix cartes de
                l'écran, dont neuf que personne n'ouvrira.
              */}
              {isOpen ? <ConversationBody reportId={reportId} /> : null}
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

function ConversationBody({ reportId }: { reportId: number }) {
  const t = useTranslations("reports");
  const { locale } = useLocale();
  const { context, isLoading, isError, isMissing } = useReportContext(
    reportId,
    true,
  );

  if (isLoading) {
    return (
      <div aria-hidden className="flex flex-col gap-3">
        {[0, 1, 2, 3].map((row) => (
          <Skeleton className="h-12 w-full" key={row} />
        ))}
      </div>
    );
  }

  // Le signalement ne vise pas un message, ou le message a disparu de la base.
  // C'est un cas normal, pas une panne — d'où le ton, et l'absence de toast.
  if (isMissing) {
    return (
      <p className="text-sm text-pretty text-muted">{t("context-none")}</p>
    );
  }

  if (isError || !context) {
    return (
      <p className="text-sm text-pretty text-muted">{t("context-failed")}</p>
    );
  }

  return (
    <>
      <p className="text-xs text-muted">
        {t(
          context.conversationType === "group"
            ? "context-group"
            : "context-direct",
        )}
      </p>

      {/* Les bornes disent ce qui n'est **pas** montré. Sans elles, une fenêtre
          de dix messages se lit comme la conversation entière. */}
      {context.hasMoreBefore ? (
        <p className="text-center text-xs text-muted">{t("context-more")}</p>
      ) : null}

      <ol className="flex flex-col gap-2">
        {context.messages.map((message) => (
          <li
            // Le message visé se distingue par le fond **et** par une puce : la
            // couleur seule ne s'annonce pas à un lecteur d'écran.
            className={`flex flex-col gap-1 rounded-lg p-3 ${
              message.isTarget
                ? "bg-accent-soft text-accent-soft-foreground"
                : "bg-surface"
            }`}
            key={message.id}
          >
            <div className="flex flex-wrap items-center gap-2">
              {/*
                Le même badge que sur la carte, et pour la même raison : dans un
                fil à trois voix, c'est la photo qui dit qui parle avant qu'on
                ait lu le pseudo. Deux rendus d'un compte auraient fini par
                diverger.

                `accountId` **et** `username` : le serveur renvoie les deux à
                `null` pour un compte supprimé, et un badge sans identifiant n'a
                pas de fiche à ouvrir.
              */}
              <span className="text-sm font-medium">
                <AccountBadge account={author(message)} isLinked />
              </span>

              <span className="text-xs text-muted">
                {relativeTime(message.createdAt, locale, Date.now())}
              </span>

              {message.isTarget ? (
                <Chip size="sm">{t("context-target")}</Chip>
              ) : null}

              {message.isReported && !message.isTarget ? (
                <Chip color="warning" size="sm">
                  {t("context-reported")}
                </Chip>
              ) : null}

              {/*
                ⚠️ Le contenu d'un message supprimé est **conservé** ici, alors
                que l'endpoint utilisateur renvoie `null`. Écart assumé du
                serveur : la suppression est le plus souvent la suite donnée au
                signalement, la masquer effacerait la preuve au moment de
                relire le dossier. D'où le barré, et non l'absence.
              */}
              {message.deletedAt ? (
                <Chip color="danger" size="sm">
                  {t("context-deleted")}
                </Chip>
              ) : null}
            </div>

            <p
              className={`text-sm text-pretty whitespace-pre-wrap ${
                message.deletedAt ? "text-muted line-through" : ""
              }`}
            >
              {message.content}
            </p>

            <MessageReactions locale={locale} reactions={message.reactions} />
          </li>
        ))}
      </ol>

      {context.hasMoreAfter ? (
        <p className="text-center text-xs text-muted">{t("context-more")}</p>
      ) : null}
    </>
  );
}

/**
 * Les réactions posées sur un message.
 *
 * Elles ne sont pas décoratives en modération : un pouce levé sur une insulte
 * change ce qu'on juge — une seule personne dérape, ou trois s'y mettent. D'où
 * l'affichage des **noms**, et pas seulement d'un compteur.
 *
 * L'ordre vient du serveur — première pose, croissant — et n'est pas retrié
 * ici : une pastille qui change de place d'une lecture à l'autre se relit à
 * chaque fois.
 *
 * ⚠️ Un emoji ne s'annonce pas. `aria-hidden` sur ce qui est visible, et la
 * phrase complète en `sr-only` : sans elle, un lecteur d'écran lit le nom
 * Unicode de l'emoji suivi d'un nombre nu, ce qui ne dit ni qui ni quoi.
 */
function MessageReactions({
  locale,
  reactions,
}: {
  locale: Locale;
  reactions: MessageReaction[];
}) {
  const t = useTranslations("reports");

  if (reactions.length === 0) return null;

  const list = new Intl.ListFormat(locale, { type: "conjunction" });

  return (
    <ul className="flex flex-wrap items-center gap-1.5 pt-1">
      {reactions.map((reaction) => {
        const names = list.format(
          reaction.accounts.map((account) => account.username),
        );

        return (
          <li key={reaction.emoji}>
            <span
              className="flex items-center gap-1 rounded-full bg-default px-2 py-0.5 text-xs text-default-foreground"
              // Au survol comme au clavier, la même chose que ce que dit le
              // lecteur d'écran — la souris n'a pas moins droit aux noms.
              title={names}
            >
              <span aria-hidden="true">{reaction.emoji}</span>
              <span aria-hidden="true" className="tabular-nums opacity-60">
                {reaction.accounts.length}
              </span>
              <span className="sr-only">
                {t("reaction-by", { emoji: reaction.emoji, names })}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
